import React, { useState, useEffect, useRef } from 'react';
import './ChatInterface.css';

const ChatInterface = ({ onRequestCreated, approvalStatus }) => {
  const [messages, setMessages] = useState([]);
  const [currentStep, setCurrentStep] = useState('initial');
  const [vmConfig, setVmConfig] = useState({
    os: '',
    size: '',
    cost: 0
  });
  const [requestId, setRequestId] = useState(null);
  const messagesEndRef = useRef(null);
  const initializedRef = useRef(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Initial greeting - only show once when component first mounts
    if (!initializedRef.current) {
      initializedRef.current = true;
      setMessages([{
        sender: 'agent',
        text: 'Hello! I can help you provision a new virtual machine. Would you like to request a VM?',
        options: {
          type: 'confirm',
          choices: [
            { label: 'Yes, please', value: 'yes' },
            { label: 'No, thanks', value: 'no' }
          ]
        },
        timestamp: new Date()
      }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (approvalStatus && approvalStatus.requestId === requestId && currentStep === 'waiting-approval') {
      if (approvalStatus.approved) {
        handleApprovalReceived(approvalStatus);
      } else {
        handleRejection(approvalStatus);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatus]);

  const addMessage = (sender, text, options = null) => {
    setMessages(prev => [...prev, { sender, text, options, timestamp: new Date() }]);
  };

  const handleUserMessage = (text) => {
    addMessage('user', text);
  };

  const handleOptionClick = (option) => {
    handleUserMessage(option.label);

    if (currentStep === 'initial' && option.value === 'yes') {
      setCurrentStep('select-os');
      setTimeout(() => {
        addMessage('agent', 'Great! Let\'s get started. Which operating system would you like?', {
          type: 'select',
          choices: [
            { label: 'Ubuntu 22.04 LTS', value: 'ubuntu' },
            { label: 'Windows Server 2022', value: 'windows' },
            { label: 'Red Hat Enterprise Linux 9', value: 'rhel' }
          ]
        });
      }, 500);
    } else if (currentStep === 'initial' && option.value === 'no') {
      setTimeout(() => {
        addMessage('agent', 'No problem! Let me know if you need anything else.');
      }, 500);
    } else if (currentStep === 'select-os') {
      setVmConfig(prev => ({ ...prev, os: option.value }));
      setCurrentStep('select-size');
      setTimeout(() => {
        addMessage('agent', 'Perfect! Now, what size VM do you need?', {
          type: 'select',
          choices: [
            { label: 'Small (2 vCPU, 4GB RAM)', value: 'small', cost: 45 },
            { label: 'Medium (4 vCPU, 8GB RAM)', value: 'medium', cost: 95 },
            { label: 'Large (8 vCPU, 16GB RAM)', value: 'large', cost: 185 }
          ]
        });
      }, 500);
    } else if (currentStep === 'select-size') {
      const cost = option.cost;
      setVmConfig(prev => ({ ...prev, size: option.value, cost }));
      setCurrentStep('show-cost');
      setTimeout(() => {
        addMessage('agent', `📊 Estimated monthly cost: $${cost}\n\nWould you like to proceed with this configuration?`, {
          type: 'confirm',
          choices: [
            { label: '✓ Yes, proceed', value: 'yes' },
            { label: '✗ Cancel', value: 'no' }
          ]
        });
      }, 500);
    } else if (currentStep === 'show-cost' && option.value === 'yes') {
      setCurrentStep('creating-request');
      const reqId = `REQ-${Math.floor(Math.random() * 10000)}`;
      setRequestId(reqId);
      setTimeout(() => {
        addMessage('agent', `✅ Service request created: ${reqId}\n\n📋 Status: Waiting for manager approval\n\nYou can track the progress at any time. I'll notify you once your manager reviews the request.`);
        onRequestCreated({
          requestId: reqId,
          os: vmConfig.os,
          size: vmConfig.size,
          cost: vmConfig.cost
        });
        setCurrentStep('waiting-approval');
      }, 1000);
    } else if (currentStep === 'show-cost' && option.value === 'no') {
      setCurrentStep('initial');
      setTimeout(() => {
        addMessage('agent', 'Request cancelled. Let me know if you need anything else!');
      }, 500);
    }
  };

  const handleApprovalReceived = (approval) => {
    setCurrentStep('provisioning');
    setTimeout(() => {
      addMessage('agent', `🎉 Great news! Your request has been approved by ${approval.approver}.\n\n🔧 Starting VM provisioning...`);
      startProvisioning();
    }, 1000);
  };

  const handleRejection = (approval) => {
    setCurrentStep('rejected');
    setTimeout(() => {
      addMessage('agent', `❌ Your request has been rejected by ${approval.approver}.\n\nPlease contact your manager for more details or submit a new request with updated requirements.`);
    }, 1000);
  };

  const startProvisioning = () => {
    const steps = [
      { delay: 2000, message: '🔍 Checking available inventory and capacity...' },
      { delay: 3000, message: '⚙️ Initiating VM provisioning...\n⏱️ Estimated completion: 3-5 minutes' },
      { delay: 4000, message: '📦 Installing monitoring agents...' },
      { delay: 3000, message: '🔒 Applying security tools and policies...' },
      { delay: 3000, message: '💾 Configuring backup and compliance settings...' },
      { delay: 3000, message: '✅ VM provisioning complete!\n\n📋 VM Details:\n• IP Address: 192.168.1.42\n• DNS Name: vm-prod-001.company.local\n• Access: RDP/SSH enabled\n\nPlease confirm that you can access the VM.' },
    ];

    let totalDelay = 0;
    steps.forEach((step, index) => {
      totalDelay += step.delay;
      setTimeout(() => {
        addMessage('agent', step.message);
        if (index === steps.length - 1) {
          setCurrentStep('confirm-access');
          setTimeout(() => {
            addMessage('agent', 'Can you confirm the VM is accessible?', {
              type: 'confirm',
              choices: [
                { label: '✓ Yes, everything works', value: 'yes' },
                { label: '✗ Having issues', value: 'no' }
              ]
            });
          }, 1000);
        }
      }, totalDelay);
    });
  };

  const handleAccessConfirmation = (option) => {
    handleUserMessage(option.label);
    if (option.value === 'yes') {
      setCurrentStep('completed');
      setTimeout(() => {
        addMessage('agent', `✅ Request ${requestId} has been successfully completed and closed.\n\n📊 Summary:\n• Request processed in minutes\n• Fully documented and traceable\n• Governance enforced automatically\n\nThank you for using our agent-driven IT operations platform!`);
      }, 1000);
    }
  };

  return (
    <div className="chat-interface">
      <div className="chat-header">
        <h2>🤖 IT Operations Agent</h2>
        <p>Agent-Driven VM Provisioning</p>
      </div>
      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.sender}`}>
            <div className="message-content">
              <div className="message-text">{msg.text}</div>
              {msg.options && (
                <div className="message-options">
                  {msg.options.choices.map((choice, i) => (
                    <button
                      key={i}
                      className="option-button"
                      onClick={() => {
                        if (currentStep === 'confirm-access') {
                          handleAccessConfirmation(choice);
                        } else {
                          handleOptionClick(choice);
                        }
                      }}
                    >
                      {choice.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
    </div>
  );
};

export default ChatInterface;

